import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { combineLatest } from "rxjs";
import { ExtendedHierarchyNode, NodeId, NodeVM, PublicationType, SchemaLevel } from "src/core/backend/controllers";
import { HierarchyCacheService } from "src/modules/shared/services/hierarchy-cache.service";
// AutoUnsubscribe removed; using takeUntilDestroyed where applicable
import { DocumentTypes } from "src/shared/utils/doc-type";

import { SharedModule } from "#modules/shared";
import { ExpandArrowComponent, ExpandArrowState } from "#modules/shared/components/expand-arrow/expand-arrow.component";

import { ContentCheckerService } from "../../../shared/services/content-check.service";
import { LibraryNavigationService, PathInfo } from "../../services/library-navigation.service";

@Component({
  selector: "app-library-navigation-node",
  templateUrl: "./library-navigation-node.component.html",
  styleUrls: ["./library-navigation-node.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, ExpandArrowComponent],
})
export class LibraryNavigationNodeComponent implements OnInit {
  private readonly elRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly cacheService = inject(HierarchyCacheService);
  private readonly navService = inject(LibraryNavigationService);
  private readonly contentChecker = inject(ContentCheckerService);
  private readonly destroyRef = inject(DestroyRef);

  readonly DocumentTypes = DocumentTypes;
  readonly SchemaLevel = SchemaLevel;

  // docId / docType / expanded are reassigned internally, so they are `model`
  // (writable) rather than read-only `input`.
  readonly docId = model<NodeId | null>(null);
  readonly docType = model<PublicationType | undefined>(undefined);
  readonly node = input.required<ExtendedHierarchyNode>();
  readonly pathInfo = input<PathInfo | null>(null);
  readonly showCaption = input(true);
  readonly canEdit = input(false);
  readonly expanded = model(false);
  private readonly loaded = signal<boolean | null>(false);

  indents: number[] = [];
  hide = true;

  // ── Derived view state (all computed from node()/pathInfo()/docId()) ──────────
  readonly isDoc = computed(() => NodeVM.isDocument(this.node()));
  readonly isLink = computed(() => !NodeVM.isOrganization(this.node()));
  readonly isChapter = computed(() => NodeVM.isChapter(this.node()));
  readonly isLevel = computed(() => NodeVM.isLevel(this.node()));
  readonly isPublication = computed(() => NodeVM.isPublication(this.node()));
  readonly isWebPage = computed(() => NodeVM.isWebPage(this.node()));
  readonly isFolder = computed(() => !this.isDoc() && !this.isChapter() && !this.isLevel());
  readonly skip = computed(() => [SchemaLevel.Archive, SchemaLevel.WebPages, SchemaLevel.WebPage].includes(this.node().nodeName));
  readonly expandable = computed(() => !!this.node()?.hasChildren);

  readonly mayNavigate = computed(
    () =>
      this.isDoc() ||
      this.isChapter() ||
      this.isPublication() ||
      this.isFolder() ||
      this.isWebPage() ||
      this.contentChecker.getContentType(this.node().content).type !== "empty"
  );

  readonly isSelectionInside = computed(() => {
    const pathInfo = this.pathInfo();
    if (!pathInfo) return false;

    const node = this.node();
    // this node *is* the selected book…
    const isSelectedBookNode = pathInfo.document?.id === node.id;
    // …or it is the parent of the active page range
    const inDocument = pathInfo.document?.id === this.docId();
    const inPageRange = inDocument && !!(pathInfo.page && node.startPage <= pathInfo.page && node.endPage >= pathInfo.page);

    return isSelectedBookNode || inPageRange;
  });

  readonly isSelected = computed(() => {
    const pathInfo = this.pathInfo();
    if (!pathInfo) return false;

    return pathInfo.nodeId === this.node().id || this.isSelectionInside();
  });

  readonly state = computed<ExpandArrowState>(() =>
    !this.expandable()
      ? ExpandArrowState.Disabled
      : this.loaded() == null
        ? ExpandArrowState.Loading
        : this.expanded()
          ? ExpandArrowState.Expanded
          : ExpandArrowState.Closed
  );

  constructor() {
    // Mirror the active node id/type back onto the writable models.
    effect(() => {
      const node = this.node();
      if (NodeVM.isPublication(node)) this.docType.set(node.id as string as PublicationType);
      if (NodeVM.isDocument(node)) this.docId.set(node.id);
    });

    // Auto-expand a document when it becomes the selected node.
    effect(() => {
      if (this.isSelected() && this.isDoc()) this.expand();
    });
  }

  ngOnInit() {
    combineLatest({
      reset: this.navService.resetNavExpansion$,
      expanded: this.navService.isExpanded(this.node()),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ reset, expanded }) => {
        if (!!expanded) this.expand();
        else if (reset || this.node().nodeName === SchemaLevel.Document) this.fold();
      });
  }

  onToggle() {
    if (this.expandable()) {
      if (!this.expanded()) {
        this.expand();
      } else {
        this.fold();
      }
    }
  }

  onExpand() {
    this.expand();
  }

  private fold() {
    this.expanded.set(false);
  }

  private expand() {
    if (!this.expandable() || this.expanded()) {
      return;
    }

    if (this.loaded() === false) {
      this.loaded.set(null);
      this.cacheService.getChildren(this.node().id).subscribe(() => {
        this.loaded.set(true);
        this.expanded.set(true);
      });
    } else if (this.loaded() === true) {
      this.expanded.set(true);
    }
  }

  onAnimationDone(event: AnimationEvent) {
    // `animationend` bubbles, and every nested node uses the same `v-expand-in`
    // animation — ignore events bubbling up from descendant nodes so only the
    // node that actually expanded scrolls into view.
    if (event.target !== event.currentTarget) return;
    if (event.animationName !== "v-expand-in") return;
    this.elRef.nativeElement.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }
}
